import { assertCommonPrintPreflight, createSingleUseSend } from "./preflight";
import {
  PrintProviderError,
  type PreparedPrint,
  type PrintProvider,
} from "./types";

export interface PreparedBrowserDialog {
  print(): void;
  dispose(): void;
}
export type PrepareBrowserDialog = (
  documentHtml: string,
) => Promise<PreparedBrowserDialog>;

function prepareIframeDialog(documentHtml: string): Promise<PreparedBrowserDialog> {
  if (typeof document === "undefined") {
    return Promise.reject(
      new PrintProviderError(
        "BROWSER_PRINT_UNAVAILABLE",
        "Browser printing requires a document.",
      ),
    );
  }

  const iframe = document.createElement("iframe");
  iframe.setAttribute("aria-hidden", "true");
  iframe.tabIndex = -1;
  // `hidden`/display:none prevents some browsers from laying out a printable
  // browsing context. Keep it renderable but outside the visible viewport.
  Object.assign(iframe.style, {
    position: "fixed",
    insetInlineStart: "-10000px",
    top: "0",
    width: "1px",
    height: "1px",
    border: "0",
    pointerEvents: "none",
  });

  return new Promise((resolve, reject) => {
    const dispose = () => iframe.remove();
    iframe.addEventListener(
      "load",
      () => {
        const print = iframe.contentWindow?.print;
        if (!print) {
          dispose();
          reject(
            new PrintProviderError(
              "BROWSER_PRINT_UNAVAILABLE",
              "The browser print dialog is unavailable.",
            ),
          );
          return;
        }
        resolve({
          print: () => print.call(iframe.contentWindow),
          dispose,
        });
      },
      { once: true },
    );
    iframe.srcdoc = documentHtml;
    document.body.append(iframe);
  });
}

export function createBrowserPrintProvider(options?: {
  prepareDialog?: PrepareBrowserDialog;
  now?: () => Date;
}): PrintProvider {
  const prepareDialog = options?.prepareDialog ?? prepareIframeDialog;
  const now = options?.now ?? (() => new Date());

  return {
    kind: "BROWSER",
    async preflight(request): Promise<PreparedPrint> {
      assertCommonPrintPreflight(request);
      if (request.hardwareActions?.cut || request.hardwareActions?.openCashDrawer) {
        throw new PrintProviderError(
          "RAW_ACTION_UNSUPPORTED",
          "Browser fallback cannot actuate cutter or cash-drawer commands.",
        );
      }

      let dialog: PreparedBrowserDialog;
      try {
        dialog = await prepareDialog(request.documentHtml ?? "");
      } catch (cause) {
        if (cause instanceof PrintProviderError) throw cause;
        throw new PrintProviderError(
          "BROWSER_PRINT_UNAVAILABLE",
          "The browser print dialog could not be prepared.",
          cause,
        );
      }

      return {
        provider: "BROWSER",
        dispose: dialog.dispose,
        send: createSingleUseSend(async ({ onBeforeDispatch }) => {
          try {
            await onBeforeDispatch();
          } catch (cause) {
            dialog.dispose();
            throw cause;
          }
          try {
            dialog.print();
            return {
              provider: "BROWSER",
              status: "FALLBACK_DIALOG_CLOSED",
              observedAt: now().toISOString(),
            };
          } catch {
            return {
              provider: "BROWSER",
              status: "UNKNOWN",
              observedAt: now().toISOString(),
              errorClass: "BROWSER_DIALOG_OUTCOME_UNKNOWN",
            };
          } finally {
            dialog.dispose();
          }
        }),
      };
    },
  };
}
